import { Box, styled, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { memo, Suspense } from "react";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { INK } from "../../theme/tokens";
import { IssuePriority } from "../../utils/models";
import { ChatBot } from "../Chat/ChatBot";
import { HeaderButton } from "../headerButton";
import { LoadingFallback } from "../LoadingFallback/LoadingFallback";
import { NurseDrawer } from "../nurseDrawer";
import { PriorityChips } from "../PriorityChips/PriorityChips";
import { RecommendationList } from "../RecommendationList/RecommendationList";
import { Recommendations } from "../Recommendations/Recommendations";
import Requests from "../RequestData/Requests";
import { MedicalDepartmentRequestsTab } from "../RequestData/RequestsTab/MedicalDepartmentRequestsTab";
import { RequestsTab } from "../RequestData/RequestsTab/RequestsTab";
import { TimeOffRequestsTab } from "../RequestData/TimeOffRequestsTab/TimeOffRequestsTab";
import { StaffCommunication } from "../StaffCommunication/StaffCommunication";
import { TabValue } from "./types";

const TabContent = styled(Box)({
	width: "100%",
	height: "100%",
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	transition: "opacity 0.15s ease-in-out",
	overflow: "auto",
});

const DrawerTabsContent = memo(
	({
		currentTab,
		selectedPriority,
		handlePriorityChange,
		priorityCounts,
		recommendationsByPriority,
		commonRecommendationProps,
		handleOpenDialog,
		setCurrentTab,
		date,
		requestId,
	}: {
		currentTab: TabValue;
		selectedPriority: IssuePriority | "all";
		handlePriorityChange: (priority: IssuePriority | "all") => void;
		priorityCounts: any;
		recommendationsByPriority: any;
		commonRecommendationProps: any;
		handleOpenDialog: () => void;
		setCurrentTab: (value: TabValue) => void;
		date?: DateTime;
		requestId?: string | null;
	}) => {
		const { isHospital, isSnfDemo, isHospitalDemo } = useHospitalData();
		const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);
		return (
			<Box sx={{ position: "relative", height: "100%", overflow: "hidden" }}>
				{currentTab === TabValue.Recommendations && (
					<TabContent>
						<ErrorBoundary>
							{isBlockRecommendationsV2Enabled && (
								<Box>
									<Box
										sx={{
											p: 0,
											m: 0,
											display: "flex",
											alignItems: "center",
											width: "100%",
										}}
									>
										<PriorityChips selectedPriority={selectedPriority} handlePriorityChange={handlePriorityChange} priorityCounts={priorityCounts} />
									</Box>
									<Box
										sx={{
											p: 0,
											pt: 2,
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											width: "100%",
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
											<Typography
												sx={{
													fontSize: "14px",
													fontWeight: 600,
													color: INK.SECONDARY,
												}}
											>
												Suggestions (
												{selectedPriority === "all"
													? priorityCounts.all
													: selectedPriority === IssuePriority.TopPriority
														? priorityCounts.high
														: selectedPriority === IssuePriority.Important
															? priorityCounts.medium
															: priorityCounts.low}
												)
											</Typography>
										</Box>
										{isHospital && <HeaderButton onPress={handleOpenDialog} state="on" text="Fix All" width="auto" secondary={true} disabled={false} />}
									</Box>
								</Box>
							)}
							<Box sx={{ px: 0, py: 0, position: "relative" }}>
								{!isBlockRecommendationsV2Enabled ? (
									<Recommendations date={date} />
								) : (
									<RecommendationList
										recommendations={recommendationsByPriority[selectedPriority === "all" ? "all" : selectedPriority]}
										commonProps={commonRecommendationProps}
										isActive={currentTab === TabValue.Recommendations}
									/>
								)}
							</Box>
						</ErrorBoundary>
					</TabContent>
				)}

				{currentTab === TabValue.Requests && (
					<TabContent>
						<Suspense fallback={<LoadingFallback />}>
							<ErrorBoundary>
								{isSnfDemo || isHospitalDemo ? <Requests isRequestedTab={true} date={date} /> : <RequestsTab date={date} requestId={requestId} />}
							</ErrorBoundary>
						</Suspense>
					</TabContent>
				)}

				{currentTab === TabValue.MedicalDepartmentRequests && (
					<TabContent>
						<Suspense fallback={<LoadingFallback />}>
							<ErrorBoundary>
								<MedicalDepartmentRequestsTab />
							</ErrorBoundary>
						</Suspense>
					</TabContent>
				)}

				{currentTab === TabValue.TimeOffRequests && (
					<TabContent>
						<ErrorBoundary>
							<TimeOffRequestsTab />
						</ErrorBoundary>
					</TabContent>
				)}

				{currentTab === TabValue.Chat && (
					<TabContent>
						<Suspense fallback={<LoadingFallback />}>
							<ErrorBoundary>
								<StaffCommunication />
							</ErrorBoundary>
						</Suspense>
					</TabContent>
				)}

				{currentTab === TabValue.Summary && (
					<TabContent>
						<Suspense fallback={<LoadingFallback />}>
							<ErrorBoundary>
								<NurseDrawer hideHeader onClose={() => setCurrentTab(TabValue.Recommendations)} />
							</ErrorBoundary>
						</Suspense>
					</TabContent>
				)}

				{currentTab === TabValue.ChatBot && (
					<TabContent>
						<Suspense fallback={<LoadingFallback />}>
							<ErrorBoundary>
								<ChatBot />
							</ErrorBoundary>
						</Suspense>
					</TabContent>
				)}
			</Box>
		);
	},
);

export default DrawerTabsContent;
